import type { TourConfig, Hotspot } from '../types'

// API Service v2 — uses public /viewer/:id endpoint (no auth required)
interface ApiServiceConfig {
  apiUrl?: string
  storageUrl?: string
}

/**
 * Clean a scene name for display:
 * - Strips common image file extensions (.jpg, .jpeg, .png, .webp, .tif, .tiff)
 * - Returns null if the result is purely numeric (e.g. "001", "1") — avoids
 *   showing raw auto-generated filenames like "001.jpg" as scene labels.
 */
function cleanSceneName(name: string | null | undefined): string | null {
  if (!name) return null
  const stripped = name.replace(/\.(jpe?g|png|webp|tiff?)$/i, '').trim()
  if (!stripped || /^\d+$/.test(stripped)) return null
  return stripped
}

/**
 * Convert a 3-D world-space position (as stored by the builder) to the
 * {pitch, yaw} spherical coordinates expected by HotspotRenderer.
 *
 * Builder stores positions as THREE.Vector3 {x, y, z} on a sphere of radius ~500.
 * HotspotRenderer.pitchYawToVector() uses the convention:
 *   x = r·cos(pitch)·sin(-yaw)
 *   y = r·sin(pitch)
 *   z = r·cos(pitch)·cos(-yaw)
 *
 * Inverting gives:
 *   pitch = arcsin(y / r)         [degrees]
 *   yaw   = −atan2(x, z)          [degrees]
 */
function xyzToPitchYaw(x: number, y: number, z: number): { pitch: number; yaw: number } {
  const r = Math.sqrt(x * x + y * y + z * z)
  if (r === 0) return { pitch: 0, yaw: 0 }
  const pitch = Math.asin(Math.max(-1, Math.min(1, y / r))) * (180 / Math.PI)
  const yaw   = -Math.atan2(x, z) * (180 / Math.PI)
  return { pitch, yaw }
}

/**
 * Normalize a hotspot from various legacy/modern formats to the viewer Hotspot interface.
 * Handles:
 *   - Builder format:  position: {x, y, z}   (3-D world coords, sphere radius ~500)
 *   - Legacy flat:     {yaw, pitch}           (top-level fields)
 *   - Already correct: position: {pitch, yaw}
 */
function normalizeHotspot(hs: any, index: number): Hotspot {
  let position: { pitch: number; yaw: number }

  if (hs.position && typeof hs.position === 'object') {
    if ('x' in hs.position && 'y' in hs.position && 'z' in hs.position) {
      // Builder format: convert {x, y, z} → {pitch, yaw}
      position = xyzToPitchYaw(
        Number(hs.position.x),
        Number(hs.position.y),
        Number(hs.position.z),
      )
    } else {
      // Already {pitch, yaw} (legacy or already-normalized)
      position = {
        pitch: Number(hs.position.pitch ?? 0),
        yaw:   Number(hs.position.yaw   ?? 0),
      }
    }
  } else {
    // Flat legacy fields
    position = {
      yaw:   Number(hs.yaw   ?? 0),
      pitch: Number(hs.pitch ?? 0),
    }
  }

  return {
    id: hs.id || `hotspot-${index}`,
    type: hs.type || 'info',
    title: hs.title ?? hs.name ?? '',
    position,
    icon: hs.icon || hs.style?.icon || 'eye',
    content: hs.content || hs.popoverContent || '',
    contentHtml: hs.contentHtml ?? hs.popoverHtml ?? true,
    targetSceneId: hs.targetSceneId || null,
    url: hs.url || hs.link || null,
    urlNewTab: hs.urlNewTab ?? true,
  }
}

export class ApiService {
  private apiUrl: string
  private storageUrl: string

  constructor(config: ApiServiceConfig = {}) {
    this.apiUrl = config.apiUrl || import.meta.env.VITE_API_URL || 'https://api-dev.virtualtoureasy.com'
    this.storageUrl = config.storageUrl || import.meta.env.VITE_STORAGE_URL || 'https://storage-dev.virtualtoureasy.com'
  }

  /**
   * Fetch tour configuration from the public viewer endpoint.
   * Supports both UUID format and legacy orgId/tourId format.
   */
  async getTourConfig(tourId: string, previewToken?: string | null): Promise<TourConfig> {
    try {
      // Determine if this is a legacy format (contains /)
      let endpoint = tourId.includes('/')
        ? `${this.apiUrl}/viewer/legacy/${tourId}`
        : `${this.apiUrl}/viewer/${tourId}`

      // Append preview token when present so the API bypasses publish/visibility checks
      if (previewToken) {
        endpoint += `?preview_token=${encodeURIComponent(previewToken)}`
      }

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      })

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Tour not found')
        }
        throw new Error('Failed to load tour configuration')
      }

      const config = await response.json()

      // Check if tour is unpublished
      if (config.unpublished) {
        return { ...config, unpublished: true } as any
      }

      // Check if tour is password-protected
      if (config.password_required) {
        return { ...config, password_required: true } as any
      }

      const storageBaseUrl = config.storage_base_url || this.storageUrl

      // Transform scenes with proper storage URLs
      if (config.scenes && Array.isArray(config.scenes)) {
        config.scenes = config.scenes.map((scene: any) => ({
          ...scene,
          id: scene.uuid || scene.id,
          title: cleanSceneName(scene.name) || scene.title || '',
          description: scene.description || null,
          image: this.resolveUrl(scene.image_url || scene.image, storageBaseUrl),
          thumbnail: scene.thumbnail_url || scene.thumbnail
            ? this.resolveUrl(scene.thumbnail_url || scene.thumbnail, storageBaseUrl)
            : undefined,
          hotspots: (Array.isArray(scene.hotspots) ? scene.hotspots : []).map((hs: any, i: number) => normalizeHotspot(hs, i)),
          audio: scene.audio_url ? {
            url: this.resolveUrl(scene.audio_url, storageBaseUrl),
            volume: scene.audio_volume ?? 1.0,
            autoplay: !!scene.audio_autoplay,
            loop: scene.audio_loop !== false,
          } : undefined,
          initialView: {
            yaw: scene.initial_yaw || 0,
            pitch: scene.initial_pitch || 0,
            zoom: scene.initial_zoom || 100,
          },
          compassNorthOffset: scene.compass_north_offset || 0,
        }))
      }

      return config
    } catch (error) {
      console.error('Error fetching tour config:', error)
      throw error
    }
  }

  /**
   * Verify tour password and get full tour config
   */
  async verifyTourPassword(tourId: string, password: string): Promise<TourConfig> {
    const endpoint = `${this.apiUrl}/viewer/${tourId}/verify`
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ password }),
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Incorrect password')
      }
      throw new Error('Failed to verify password')
    }

    const config = await response.json()
    const storageBaseUrl = config.storage_base_url || this.storageUrl

    // Transform scenes (same as getTourConfig)
    if (config.scenes && Array.isArray(config.scenes)) {
      config.scenes = config.scenes.map((scene: any) => ({
        ...scene,
        id: scene.uuid || scene.id,
        title: cleanSceneName(scene.name) || scene.title || '',
        description: scene.description || null,
        image: this.resolveUrl(scene.image_url || scene.image, storageBaseUrl),
        thumbnail: scene.thumbnail_url || scene.thumbnail
          ? this.resolveUrl(scene.thumbnail_url || scene.thumbnail, storageBaseUrl)
          : undefined,
        hotspots: (Array.isArray(scene.hotspots) ? scene.hotspots : []).map((hs: any, i: number) => normalizeHotspot(hs, i)),
        audio: scene.audio_url ? {
          url: this.resolveUrl(scene.audio_url, storageBaseUrl),
          volume: scene.audio_volume ?? 1.0,
          autoplay: !!scene.audio_autoplay,
          loop: scene.audio_loop !== false,
        } : undefined,
        initialView: {
          yaw: scene.initial_yaw || 0,
          pitch: scene.initial_pitch || 0,
          zoom: scene.initial_zoom || 100,
        },
        compassNorthOffset: scene.compass_north_offset || 0,
      }))
    }

    return config
  }

  /**
   * Resolve a URL - if it's already absolute, return as-is.
   * Otherwise prepend the storage base URL.
   */
  private resolveUrl(path: string | null | undefined, baseUrl: string): string {
    if (!path) return ''
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path
    }
    return `${baseUrl}/${path.replace(/^\//, '')}`
  }
}
